import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Heart toggle with the app's conscience built in:
 * - poisoned products cannot be favorited (firm but loving toast)
 * - seeds-only products require an informed-consent dialog
 * - unfavoriting is always allowed — coming to your senses is free
 *
 * Props: product, favorited (bool), onChanged({favorited, favorites_count}),
 * size ('sm' card variant | 'lg' detail variant).
 */
const FavoriteButton = ({ product, favorited, onChanged, size = 'sm' }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const callApi = async () => {
    try {
      const { data } = favorited
        ? await axios.delete(`${API}/products/${product.id}/favorite`)
        : await axios.post(`${API}/products/${product.id}/favorite`);
      onChanged({ favorited: data.favorited, favorites_count: data.favorites_count });
    } catch (error) {
      console.error('Favorite error:', error);
      toast.error('Could not update favorite. Please try again.');
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!favorited && product.status === 'poisoned') {
      if (product.seed_only) {
        setConfirmOpen(true);
        return;
      }
      toast.error('Sorry, but no. I cannot allow this.', {
        description: 'This product contains cilantro. Favoriting the enemy is where I draw the line. 🌿🚫',
      });
      return;
    }
    await callApi();
  };

  const count = product.favorites_count || 0;

  return (
    <>
      <button
        onClick={handleClick}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          background: 'none', border: 'none', cursor: 'pointer',
          color: favorited ? '#e53935' : 'hsl(var(--muted-foreground))',
          fontSize: size === 'lg' ? '1rem' : '0.9rem',
        }}
        data-testid={`favorite-${product.id}`}
      >
        <Heart className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} fill={favorited ? 'currentColor' : 'none'} />
        {count > 0 && count.toLocaleString()}
      </button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>⚠️ A word of warning</DialogTitle>
            <DialogDescription>
              I want to warn you: this product contains cilantro. Admittedly,
              only the seeds… but still.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="seed-confirm-cancel">
              You're right, thanks for warning me
            </Button>
            <Button
              style={{ background: '#e65100', color: 'white' }}
              onClick={async () => { setConfirmOpen(false); await callApi(); }}
              data-testid="seed-confirm-accept"
            >
              I have no regard for my tastebuds
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FavoriteButton;
